/**
 * PaymentResultScreen — terminal-state UI shown once the customer has
 * submitted their UTR, or once the merchant has approved/rejected.
 *
 * Deliberately extracted from PaymentPage.jsx (which was 1379 LOC) to
 * cut cognitive load and let this UI evolve independently — merchants
 * frequently ask for tweaks here (custom copy, brand logos, retry CTAs)
 * without wanting to touch the payment-input flow.
 *
 * Props:
 *   data:           txn snapshot from /public/payment-page/:id
 *   statusInfo:     poll result from /public/payment-page/:id/status
 *   utr:            the UTR the customer typed (may differ from data.utr)
 *   redirectIn:     countdown seconds until auto-redirect (null = no redirect)
 *   redirectBroken: true → we've tried and failed to preload merchant URL
 */
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { fmtINR } from "@/lib/api";
import ConfettiBurst from "@/components/payment/ConfettiBurst";

export default function PaymentResultScreen({ data, statusInfo, utr, redirectIn, redirectBroken }) {
    const effectiveStatus = statusInfo?.status || data.status;
    const isSuccess = effectiveStatus === "success";
    const isFailed = effectiveStatus === "failed";

    return (
        <div className="public-bg min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
            {isSuccess && <ConfettiBurst />}
            <div className="card p-8 max-w-md w-full text-center relative" data-testid="payment-result-card">
                {isSuccess ? (
                    <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4 pop-in ring-pulse">
                        <svg viewBox="0 0 52 52" className="w-12 h-12 text-emerald-600" fill="none">
                            <circle cx="26" cy="26" r="24" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                            <path className="check-stroke" d="M14 27 L23 36 L39 18" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                ) : isFailed ? (
                    <div className="w-20 h-20 rounded-full bg-rose-100 mx-auto flex items-center justify-center mb-4 pop-in">
                        <span className="text-3xl text-rose-600 font-bold">×</span>
                    </div>
                ) : (
                    <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                        <Check className="w-7 h-7 text-emerald-600" />
                    </div>
                )}
                <h2 className="text-2xl font-bold mb-1" data-testid="payment-status-title">
                    {isSuccess ? "Payment received" : isFailed ? "Payment rejected" : "Payment submitted"}
                </h2>
                <p className="text-sm text-slate-500 mb-5">
                    {isSuccess ? "Your payment has been verified and credited."
                        : isFailed ? "We couldn't verify this payment. Please contact support."
                        : "We're verifying your payment. You'll be updated shortly."}
                </p>
                <div className="text-left text-sm space-y-1 bg-slate-50 rounded-md p-3 font-mono">
                    <div>Amount: <span className="font-semibold">{fmtINR(data.amount)}</span></div>
                    <div>UTR: <span className="font-semibold">{utr || data.utr}</span></div>
                    <div>Txn: <span className="text-xs">{data.txn_id}</span></div>
                </div>
                {!isSuccess && !isFailed && (
                    <div className="text-[11px] text-slate-400 mt-4 flex items-center justify-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" /> Waiting for confirmation
                    </div>
                )}
                {(isSuccess || isFailed) && statusInfo?.redirect_url && (
                    <div className="mt-5">
                        {redirectBroken ? (
                            /* Broken-redirect path. We DON'T claim "your
                               merchant's site is down" — from a single
                               client that's unverifiable (could be network,
                               captive portal, DNS resolver). Just tell them
                               the confirmed part (their payment is safe)
                               and offer a manual try-anyway affordance. */
                            <div data-testid="redirect-broken-note"
                                 className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                                <div className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    Your payment is confirmed
                                </div>
                                <div className="text-[11px] text-amber-700 leading-snug mb-2.5">
                                    We couldn&apos;t automatically reach the merchant&apos;s site
                                    right now, so we&apos;ve stopped the redirect to keep your
                                    confirmation visible. Your UTR <span className="font-mono font-semibold">{utr || data.utr}</span> is
                                    the proof of payment — you can safely close this tab.
                                </div>
                                <a href={statusInfo.redirect_url}
                                   data-testid="redirect-link-broken"
                                   className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 underline hover:text-amber-900">
                                    Try opening the merchant page anyway →
                                </a>
                            </div>
                        ) : (
                            <>
                                <a href={statusInfo.redirect_url} data-testid="redirect-link"
                                   className="btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm">
                                    Continue {redirectIn != null && redirectIn > 0 ? `(${redirectIn})` : ""}
                                </a>
                                <div className="text-[11px] text-slate-400 mt-2">Redirecting back to merchant…</div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
